import { ClipboardList, Clock, Handshake, Lock, ExternalLink, CheckCircle2, Hourglass } from "lucide-react";

const ROWS = [
  { key: "recon", icon: ClipboardList, title: "Reconditioning report", teaser: "What the shop did to this vehicle before it hit the lot" },
  { key: "time_on_lot", icon: Clock, title: "Time on lot", teaser: "How long this vehicle has been here" },
  { key: "acquisition", icon: Handshake, title: "How we got this vehicle", teaser: "Trade-in, lease return, auction…" },
];

function Unlocked({ kind, data }) {
  if (kind === "recon") {
    const Icon = data.status === "completed" ? CheckCircle2 : Hourglass;
    return (
      <div className="space-y-2" data-testid="insider-recon-unlocked">
        <div className="flex items-start gap-2 text-sm text-slate-200"><Icon className={`h-4 w-4 mt-0.5 shrink-0 ${data.status === "completed" ? "text-emerald-400" : "text-amber-300"}`} /> {data.label}</div>
        {data.notes && <p className="text-sm text-slate-300 whitespace-pre-line">{data.notes}</p>}
        {data.file_url && (
          <a href={data.file_url} target="_blank" rel="noopener" data-testid="insider-recon-open" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-soft hover:text-white">
            Open the report <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    );
  }
  if (kind === "time_on_lot") {
    return (
      <div data-testid="insider-lot-unlocked">
        <div className="font-heading text-2xl font-black text-brand">{data.label}</div>
        {data.since && <div className="text-xs text-slate-400">Arrived {new Date(`${data.since}T12:00:00`).toLocaleDateString(undefined, { month: "long", day: "numeric" })}</div>}
      </div>
    );
  }
  return (
    <div data-testid="insider-acquisition-unlocked">
      <div className="text-sm font-semibold text-slate-100">{data.label}</div>
      {data.notes && <p className="text-sm text-slate-300 mt-1 whitespace-pre-line">{data.notes}</p>}
    </div>
  );
}

// "Insider details" card on the QR page: dealer-selected walls, each a locked row until the shopper leaves contact info.
export function InsiderDetails({ vehicle: v, dealer, unlocked, onWall }) {
  const avail = v.insider || {};
  const rows = ROWS.filter((r) => avail[r.key]);
  if (!rows.length) return null;
  const open = unlocked?.insider || null;
  return (
    <div className="rounded-2xl border border-[#2A2E3D] bg-[#12141C] p-5" data-testid="insider-details">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech">Insider details from {dealer.name}</div>
      <div className="mt-3 space-y-2">
        {rows.map((r) => {
          const data = open?.[r.key];
          return data ? (
            <div key={r.key} className="rounded-xl border border-brand/30 bg-brand/5 px-4 py-3" data-testid={`insider-row-${r.key}`}>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-400 font-mono-tech mb-1.5"><r.icon className="h-3.5 w-3.5 text-brand" /> {r.title}</div>
              <Unlocked kind={r.key} data={data} />
            </div>
          ) : (
            <button
              key={r.key}
              type="button"
              onClick={() => onWall(r.key)}
              data-testid={`insider-wall-${r.key}`}
              className="w-full flex items-center gap-3 rounded-xl border border-dashed border-slate-600 bg-slate-900/50 px-4 py-3 text-left hover:border-brand hover:bg-brand/10 transition-colors"
            >
              <r.icon className="h-4 w-4 text-brand shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-100">{r.title}</div>
                <div className="text-[11px] text-slate-400 truncate">{r.teaser}</div>
              </div>
              <Lock className="h-4 w-4 text-slate-500 shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
